import React, { useState, useEffect, useRef } from 'react';
import Button from '@material-ui/core/Button';
import PlatformsTab from './PlatformsTab';
import { ctaItems } from './config';
import { isMobile } from '@/biz/tool';
import MobileLine from './MobileLine';
import MobileSteps from './MobileSteps';
import { useTranslation } from 'react-i18next';

interface Props {
  step: number;
  setStep: Function;
  platform: string;
  maxStep: number;
  setMaxStep: Function;
}

const Cta: React.FC<Props> = ({
  step,
  setStep,
  platform,
  maxStep,
  setMaxStep
}) => {
  const { t } = useTranslation();
  const [time, setTime] = useState(10);
  const timeRef = useRef(time);

  // 同步 time 到 ref
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    const educationModalEnd = localStorage.getItem('educationModalEnd');

    // 如果已经完成或已结束，直接设置为0
    if (maxStep > step || !!educationModalEnd) {
      setTime(0);
      return;
    }

    // 如果时间大于0，开始倒计时
    if (timeRef.current > 0) {
      const timer = setTimeout(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [time, maxStep, step]); // 添加所有依赖项

  const [currentPlatform, setCurrentPlatform] = useState(platform);

  return (
    <div
      className={`w-full bg-white pb-8 flex flex-col justify-between ${isMobile() ? 'px-0' : 'px-8'}`}
    >
      <div
        className={`w-full py-5 inline-flex flex-col justify-center items-start gap-2 ${isMobile() ? '' : ' border-b border-[#e8e8ec]'}`}
      >
        <div className="justify-start text-[#1c2024] text-2xl font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
          {t('education.cta.title', { defaultValue: 'CTA requirements' })}
        </div>
        {isMobile() && <MobileSteps step={step} />}
        <div className="w-full justify-center text-[#60646c] text-sm font-normal font-['Inter'] leading-tight">
          {t('education.cta.description', {
            defaultValue:
              'Achieving high conversion rates enhances your collaboration opportunities on Head Creator. Discover how to drive optimal conversion results.'
          })}
        </div>
      </div>
      {isMobile() && <MobileLine />}
      <div className={`${isMobile() ? '' : 'h-[500px] pb-8 overflow-y-auto'}`}>
        <PlatformsTab
          platform={currentPlatform}
          setPlatform={setCurrentPlatform}
        />
        <div className="w-full mt-6 flex items-center justify-center">
          <img
            src={ctaItems?.[currentPlatform]?.img}
            className="w-full"
            alt=""
          />
        </div>
        <div className="self-stretch px-2 inline-flex flex-col justify-start items-start gap-3 mt-6">
          <div className="self-stretch justify-start">
            <span className="text-[#1c2024] text-base font-semibold font-['Inter'] leading-7">
              {t('education.cta.notes_title', { defaultValue: 'Notes:' })}
              <br />
            </span>
            <ul className="list-disc pl-4 fuck">
              {ctaItems?.[currentPlatform]?.notes?.map((item: string) => (
                <li key={item}>
                  <span
                    dangerouslySetInnerHTML={{ __html: item }}
                    className="text-[#1c2024] text-base font-normal font-['Inter'] leading-7"
                  ></span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-4 mt-8">
        <Button
          className="mui-btn-gray !w-[160px] !h-[40px]"
          onClick={() => setStep(step - 1)}
        >
          {t('education.cta.back_btn', { defaultValue: 'Back' })}
        </Button>
        <Button
          className="mui-btn-primary !w-[160px] !h-[40px]"
          onClick={() => {
            setStep(step + 1);
            setMaxStep(Math.max(maxStep, step + 1));
          }}
          disabled={time > 0}
        >
          {time > 0
            ? t('education.cta.next_btn_with_time', {
                defaultValue: 'Next ({{time}}s)',
                time
              })
            : t('education.cta.next_btn', { defaultValue: 'Next' })}
        </Button>
      </div>
    </div>
  );
};

export default Cta;
